// Pieces the Holiday planner and the Term planner share: the styles, a labelled field, a card with a heading, a line of the results, the row of days a programme runs, and
// a row of weekly number boxes.
export const inputCls = "w-full rounded-lg border border-[var(--hp-border-hi)] bg-[var(--hp-bg)] py-1.5 px-2 text-xs text-[var(--hp-heading)] outline-none focus:border-[var(--hp-primary)] transition-colors";
export const numCls = `${inputCls} font-mono-num text-right`;
export const cardCls = "rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)]";
export const btnCls = "inline-flex items-center gap-2 rounded-full border border-[var(--hp-border-hi)] bg-[var(--hp-card)] px-4 py-2 text-xs font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-card-hover)] transition-colors disabled:opacity-40";
export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];

export const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
export const keep = (v) => (v === null || v === undefined ? "" : String(v));

export function Field({ label, children, hint, className = "" }) {
  return (
    <label className={`block text-xs ${className}`}>
      <span className="font-semibold text-[var(--hp-muted)]">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <span className="block mt-1 text-[11px] text-[var(--hp-muted)]">{hint}</span>}
    </label>
  );
}

export function Section({ title, note, children, testId }) {
  return (
    <section className={`${cardCls} p-5 space-y-3`} data-testid={testId}>
      <div><h4 className="font-head text-sm font-semibold text-[var(--hp-heading)]">{title}</h4>{note && <p className="text-xs text-[var(--hp-muted)] mt-0.5">{note}</p>}</div>
      {children}
    </section>
  );
}

export function Line({ label, value, sub, strong, tone, testId }) {
  return (
    <div className={`flex items-baseline justify-between gap-3 ${strong ? "pt-1.5 border-t border-[var(--hp-border)] font-semibold" : ""}`} data-testid={testId}>
      <span className="text-[var(--hp-text)] min-w-0">{label}{sub && <span className="block text-[11px] font-normal text-[var(--hp-muted)]">{sub}</span>}</span>
      <span className={`font-mono-num shrink-0 ${tone === "neg" ? "text-[var(--hp-loss)]" : tone === "pos" ? "text-[var(--hp-profit)]" : "text-[var(--hp-heading)]"}`}>{value}</span>
    </div>
  );
}

// The weekdays between the plan's dates, a week to a row. A day is open unless it is a public holiday (opened by hand) or has been closed by hand; clicking flips it.
export function DayChips({ result, draft, set, hint }) {
  if (!result) return null;
  const toggle = (d) => {
    const closed = new Set(draft.closed), opened = new Set(draft.opened);
    if (d.open) { if (opened.has(d.date)) opened.delete(d.date); else closed.add(d.date); }
    else if (closed.has(d.date)) closed.delete(d.date); else opened.add(d.date);
    set({ closed: [...closed], opened: [...opened] });
  };
  return (
    <div className="space-y-1.5" data-testid="planner-days">
      <p className="text-xs font-semibold text-[var(--hp-muted)]">{hint || "Days it runs: click a day to open or close it. Public holidays start closed; open them if you run."}</p>
      {Array.from({ length: result.weeks }, (_, w) => (
        <div key={w} className="flex flex-wrap items-center gap-1.5">
          <span className="w-24 text-[11px] text-[var(--hp-muted)] font-mono-num">{result.weekly[w]?.label}</span>
          {result.dates.filter((x) => x.week === w).map((d) => (
            <button key={d.date} type="button" onClick={() => toggle(d)} title={d.holiday || ""} data-testid={`planner-day-${d.date}`} aria-pressed={d.open}
              className={`rounded-lg px-2.5 py-1 text-xs font-semibold border transition-colors ${d.open ? "bg-[var(--hp-primary)] text-[#04231a] border-transparent" : "border-[var(--hp-border-hi)] text-[var(--hp-muted)] line-through hover:text-[var(--hp-heading)]"}`}>
              {WEEKDAYS[d.weekday]} {Number(d.date.slice(8))}{d.holiday ? " ·PH" : ""}
            </button>
          ))}
        </div>
      ))}
      <p className="text-xs text-[var(--hp-muted)]"><b data-testid="planner-open-days">{result.openDays}</b> open days over {result.weeks} week{result.weeks === 1 ? "" : "s"}.</p>
    </div>
  );
}

// One box per week for a row of weekly numbers, kept exactly n long; the first box can be copied across the rest.
export function WeekCells({ values, n, onChange, testId, copy = true }) {
  const cells = Array.from({ length: n }, (_, w) => values[w] ?? "");
  return (
    <>
      {cells.map((v, w) => (
        <td key={w} className="px-0.5"><input className={`${numCls} !px-1 min-w-[3rem]`} inputMode="decimal" value={v} data-testid={`${testId}-w${w}`}
          onChange={(e) => { const next = [...cells]; next[w] = e.target.value; onChange(next); }} /></td>
      ))}
      <td className="px-1 whitespace-nowrap">{copy && <button type="button" className="text-[11px] font-semibold text-[var(--hp-primary)] hover:underline" data-testid={`${testId}-copy`} title="Copy the first week's figure into every week"
        onClick={() => onChange(Array(n).fill(cells[0]))}>wk 1 across</button>}</td>
    </>
  );
}
